import React, { useState, useEffect } from 'react';
import { Download, FileText, Presentation, Clock, CheckCircle, XCircle, RefreshCw, Palette, Eye } from 'lucide-react';

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [generationStatus, setGenerationStatus] = useState({});
  const [loadingGeneration, setLoadingGeneration] = useState({});
  const [brandColors, setBrandColors] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('professional');
  const [customColors, setCustomColors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    fetchBrandColors();
    fetchTemplates();
    fetchDocuments();
  }, []);

  const fetchBrandColors = async () => {
    try {
      const response = await fetch('/api/pdf/brand-colors');
      const data = await response.json();
      setBrandColors(data);
    } catch (error) {
      console.error('Failed to fetch brand colors:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/pdf/templates');
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchDocuments = async () => {
    // Mock data for now - in production, fetch actual user documents
    setDocuments([]);
  };

  const generatePDF = async (documentType) => {
    try {
      setLoadingGeneration(prev => ({ ...prev, [documentType]: true }));

      const requestBody = {
        document_type: documentType,
        template_style: selectedTemplate,
        custom_colors: Object.keys(customColors).length > 0 ? customColors : null
      };

      const response = await fetch('/api/pdf/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to start PDF generation');
      }

      const data = await response.json();
      
      // Start polling for status
      pollGenerationStatus(data.generation_id, documentType);

    } catch (error) {
      console.error('Failed to generate PDF:', error);
      setLoadingGeneration(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const pollGenerationStatus = async (generationId, documentType) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/pdf/status/${generationId}`);
        const status = await response.json();

        setGenerationStatus(prev => ({
          ...prev,
          [documentType]: status
        }));

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setLoadingGeneration(prev => ({ ...prev, [documentType]: false }));
          
          // Fetch download info
          const downloadResponse = await fetch(`/api/pdf/download/${generationId}`);
          const downloadData = await downloadResponse.json();
          
          // Add to documents list
          setDocuments(prev => [...prev, {
            id: downloadData.document_id,
            type: documentType,
            name: downloadData.file_name,
            size: downloadData.file_size_mb,
            generated_at: downloadData.generated_at,
            download_url: downloadData.download_url,
            brand_colors: downloadData.brand_colors,
            page_count: downloadData.page_count
          }]);
          
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setLoadingGeneration(prev => ({ ...prev, [documentType]: false }));
        }
      } catch (error) {
        console.error('Failed to poll status:', error);
        clearInterval(pollInterval);
        setLoadingGeneration(prev => ({ ...prev, [documentType]: false }));
      }
    }, 2000);
  };

  const downloadDocument = (downloadUrl, filename) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const retryGeneration = async (generationId, documentType) => {
    try {
      const response = await fetch(`/api/pdf/retry/${generationId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setLoadingGeneration(prev => ({ ...prev, [documentType]: true }));
        pollGenerationStatus(generationId, documentType);
      }
    } catch (error) {
      console.error('Failed to retry generation:', error);
    }
  };

  const formatFileSize = (sizeInMB) => {
    if (sizeInMB < 1) {
      return `${Math.round(sizeInMB * 1024)} KB`;
    }
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status?.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
      case 'queued':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Strategic Documents</h1>
          <p className="text-lg text-gray-600">
            Generate professional PDFs of your brand strategy with dynamic brand styling
          </p>
        </div>

        {/* Brand Colors Section */}
        {brandColors && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Palette className="h-5 w-5 mr-2 text-indigo-600" />
                Brand Colors
              </h2>
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
              >
                <Eye className="h-4 w-4 mr-1" />
                {showColorPicker ? 'Hide' : 'Customize'}
              </button>
            </div>
            
            {brandColors.colors_extracted && (
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex space-x-2">
                  {Object.entries(brandColors.brand_colors).map(([colorName, colorValue]) => (
                    <div key={colorName} className="text-center">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: colorValue }}
                      ></div>
                      <p className="text-xs text-gray-600 mt-1 capitalize">{colorName}</p>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-600">
                  <p>Extracted with {Math.round(brandColors.extraction_confidence * 100)}% confidence</p>
                  <p>From Expression Anchor analysis</p>
                </div>
              </div>
            )}

            {showColorPicker && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-700 font-medium">Custom Brand Colors</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {['primary', 'secondary', 'accent', 'text', 'background'].map((colorType) => (
                    <div key={colorType}>
                      <label className="block text-xs text-gray-600 mb-1 capitalize">
                        {colorType}
                      </label>
                      <input
                        type="color"
                        value={customColors[colorType] || brandColors.brand_colors?.[colorType] || '#6366f1'}
                        onChange={(e) => setCustomColors(prev => ({
                          ...prev,
                          [colorType]: e.target.value
                        }))}
                        className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Template Selection */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Template Style</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {templates.map((template) => (
              <div
                key={template.template_name}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedTemplate === template.template_name
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedTemplate(template.template_name)}
              >
                <h3 className="font-medium text-gray-900 capitalize mb-2">
                  {template.template_name}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {template.template_description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {template.suitable_for.map((type) => (
                    <span
                      key={type}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {type.replace('_', ' ')}
                    </span>
                  ))}
                </div>
                {template.color_customizable && (
                  <div className="mt-2 flex items-center text-xs text-green-600">
                    <Palette className="h-3 w-3 mr-1" />
                    Color customizable
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Document Generation */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Generate Documents</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Brand Strategy PDF */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <FileText className="h-8 w-8 text-indigo-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Brand Strategy PDF</h3>
                  <p className="text-sm text-gray-600">Complete 15-20 page strategic foundation</p>
                </div>
              </div>
              
              <ul className="text-sm text-gray-600 mb-6 space-y-1">
                <li>• Four Strategic Anchors analysis</li>
                <li>• Executive summary and insights</li>
                <li>• Professional brand-colored styling</li>
                <li>• Implementation guidelines</li>
              </ul>

              {generationStatus.brand_strategy && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(generationStatus.brand_strategy)}
                      <span className="ml-2 text-sm font-medium capitalize">
                        {generationStatus.brand_strategy.status}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {generationStatus.brand_strategy.progress_percentage}%
                    </span>
                  </div>
                  
                  {generationStatus.brand_strategy.current_step && (
                    <p className="text-xs text-gray-500 mt-1">
                      {generationStatus.brand_strategy.current_step}
                    </p>
                  )}
                  
                  {generationStatus.brand_strategy.status === 'failed' && (
                    <button
                      onClick={() => retryGeneration(generationStatus.brand_strategy.generation_id, 'brand_strategy')}
                      className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Retry Generation
                    </button>
                  )}
                </div>
              )}
              
              <button
                onClick={() => generatePDF('brand_strategy')}
                disabled={loadingGeneration.brand_strategy}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loadingGeneration.brand_strategy ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Brand Strategy PDF
                  </>
                )}
              </button>
            </div>

            {/* Pitch Deck PDF */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Presentation className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Investor Pitch Deck</h3>
                  <p className="text-sm text-gray-600">Professional 12-15 slide presentation</p>
                </div>
              </div>
              
              <ul className="text-sm text-gray-600 mb-6 space-y-1">
                <li>• Investor-focused narrative</li>
                <li>• Strategic highlights and vision</li>
                <li>• Market opportunity analysis</li>
                <li>• Brand-aligned visual design</li>
              </ul>

              {generationStatus.pitch_deck && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(generationStatus.pitch_deck)}
                      <span className="ml-2 text-sm font-medium capitalize">
                        {generationStatus.pitch_deck.status}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {generationStatus.pitch_deck.progress_percentage}%
                    </span>
                  </div>
                  
                  {generationStatus.pitch_deck.current_step && (
                    <p className="text-xs text-gray-500 mt-1">
                      {generationStatus.pitch_deck.current_step}
                    </p>
                  )}
                  
                  {generationStatus.pitch_deck.status === 'failed' && (
                    <button
                      onClick={() => retryGeneration(generationStatus.pitch_deck.generation_id, 'pitch_deck')}
                      className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Retry Generation
                    </button>
                  )}
                </div>
              )}
              
              <button
                onClick={() => generatePDF('pitch_deck')}
                disabled={loadingGeneration.pitch_deck}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loadingGeneration.pitch_deck ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Presentation className="h-4 w-4 mr-2" />
                    Generate Pitch Deck PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Generated Documents */}
        {documents.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Documents</h2>
            
            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    {doc.type === 'brand_strategy' ? (
                      <FileText className="h-8 w-8 text-indigo-600 mr-4" />
                    ) : (
                      <Presentation className="h-8 w-8 text-purple-600 mr-4" />
                    )}
                    
                    <div>
                      <h3 className="font-medium text-gray-900">{doc.name}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>{formatFileSize(doc.size)}</span>
                        <span>{doc.page_count} pages</span>
                        <span>Generated {formatDate(doc.generated_at)}</span>
                      </div>
                      
                      {doc.brand_colors && (
                        <div className="flex space-x-1 mt-2">
                          {Object.values(doc.brand_colors).slice(0, 3).map((color, index) => (
                            <div
                              key={index}
                              className="w-4 h-4 rounded-full border border-gray-300"
                              style={{ backgroundColor: color }}
                            ></div>
                          ))}
                          <span className="text-xs text-gray-500 ml-2">Brand styled</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => downloadDocument(doc.download_url, doc.name)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {documents.length === 0 && !Object.keys(generationStatus).length && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
            <p className="text-gray-600 mb-6">
              Generate your first strategic document to get professional PDFs of your brand strategy
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => generatePDF('brand_strategy')}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <FileText className="h-5 w-5 mr-2" />
                Generate Brand Strategy
              </button>
              <button
                onClick={() => generatePDF('pitch_deck')}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Presentation className="h-5 w-5 mr-2" />
                Generate Pitch Deck
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;